import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { useDataHooks } from '@wix/data-hooks-react';
import { textTyperDataHooks } from './TextTyper.dataHooks';
import { StyledText } from './StyledText';

type TextTyperProps = {
  dataHook?: string;
  text: string;
  typingSpeed?: number;
  delay?: number;
  byLetter?: boolean;
  onTypingEnd?: () => void;
  onTyping?: () => void;
};

export const TextTyper: React.FC<TextTyperProps> = ({
  dataHook,
  text,
  typingSpeed = 10,
  delay = 0,
  byLetter = false,
  onTypingEnd = () => {},
  onTyping = () => {},
}) => {
  const dataHooks = useDataHooks(textTyperDataHooks, dataHook);
  /* console.log(
    'textTyperDataHooks>>>>>>',
    dataHook,
  );*/
  const [displayText, setDisplayText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(1);
  const [isTypingEnd, setIsTypingEnd] = useState(false);
  const indexJump = byLetter ? 1 : 7;

  useEffect(() => {
    if (currentIndex < text.length) {
      const timeoutDuration =
        (text.slice(currentIndex - 1, currentIndex) === ' ' ? 2 : 1) *
        typingSpeed;
      const timeoutId = setTimeout(() => {
        setCurrentIndex(currentIndex + indexJump);

        onTyping();

        if (currentIndex >= text.length - indexJump) {
          onTypingEnd();
          setIsTypingEnd(true);
        }
      }, timeoutDuration);

      return () => {
        clearTimeout(timeoutId);
      };
    }
    return () => {};
  }, [currentIndex, text, typingSpeed, indexJump, onTypingEnd, onTyping]);

  useEffect(() => {
    setTimeout(() => setDisplayText(text.slice(0, currentIndex)), delay);
  }, [currentIndex, text, delay]);

  return (
    <StyledText>
      <span
        data-hook={dataHooks.base({ isTypingEnd })}
        data-is-typing-end={isTypingEnd}
      >
        <ReactMarkdown
          components={{
            a: ({ node, ...props }) => (
              <a {...props} target="_blank">
                {props.children}
              </a>
            ),
          }}
        >
          {ensureClosedMarkdown(displayText)}
        </ReactMarkdown>
      </span>
    </StyledText>
  );
};

function ensureClosedMarkdown(input: string): string {
  let output = input.trim();

  const markdownTags = [
    { open: '**', close: '**' },
    { open: '*', close: '*' },
    { open: '~~', close: '~~' },
    { open: '```', close: '\n```' },
    { open: '`', close: '`' },
    { open: '[', close: ']', assymetric: true }, // For links, but doesn't add a (link) part
    // Add more as required
  ];

  for (const tag of markdownTags) {
    const openCount = (
      input.match(new RegExp(escapeRegExp(tag.open), 'g')) || []
    ).length;

    const closeCount = tag.assymetric
      ? (input.match(new RegExp(escapeRegExp(tag.close), 'g')) || []).length
      : 0;

    const tagCount = tag.assymetric ? openCount + closeCount : openCount;
    const shouldAddCloseTag = tagCount % 2 !== 0;

    if (shouldAddCloseTag) {
      output += tag.close;
    }
  }

  // TODO: fix only for bold. meeds to make this generic
  output = output.replace(/\*{3,}/g, '**');
  return output;
}

function escapeRegExp(string: string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
